import React, { useEffect, useState } from "react";
import {
  Container,
  Tabs,
  Title,
  Group,
  Badge,
  Paper,
  Stack,
  Text,
  Stepper,
  Button,
  Skeleton,
} from "@mantine/core";
import {
  IconPhone,
  IconSettings,
  IconHistory,
  IconCheck,
  IconMessage,
} from "@tabler/icons-react";
import { VoiceAIProvider, useVoiceAI } from "../../context/VoiceAIContext";
import CallsTab from "./CallsTab";
import PhoneNumbersTab from "./PhoneNumbersTab";
import SettingsTab from "./SettingsTab";
import MessagesTab from "./MessagesTab";

function SetupWizard({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(0);
  const { phoneNumbers, fetchPhoneNumbers, settings, fetchSettings } =
    useVoiceAI();

  // Auto-advance when phone number is added
  useEffect(() => {
    if (phoneNumbers.length > 0 && step === 0) {
      setStep(1);
    }
  }, [phoneNumbers, step]);

  // Auto-advance when settings are saved
  useEffect(() => {
    if (settings?.greeting && step === 1) {
      setStep(2);
    }
  }, [settings, step]);

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Set Up Voice AI</Title>
          <Text c="dimmed" mt="xs">
            Get your AI receptionist answering calls in minutes.
          </Text>
        </div>

        <Stepper active={step} onStepClick={setStep}>
          <Stepper.Step label="Phone Number" description="Pick a number">
            <Paper p="lg" mt="md" withBorder>
              <PhoneNumbersTab />
            </Paper>
          </Stepper.Step>

          <Stepper.Step label="Settings" description="Customize your AI">
            <Paper p="lg" mt="md" withBorder>
              <SettingsTab />
            </Paper>
          </Stepper.Step>

          <Stepper.Completed>
            <Paper p="xl" mt="md" withBorder ta="center">
              <IconCheck size={48} color="teal" />
              <Title order={3} mt="md">
                You're all set!
              </Title>
              <Text c="dimmed" mt="xs">
                Your AI receptionist is ready. Callers to your Voice AI number
                will be greeted by your AI assistant, which can check
                availability and book appointments.
              </Text>
              <Button mt="lg" onClick={onComplete}>
                Go to Dashboard
              </Button>
            </Paper>
          </Stepper.Completed>
        </Stepper>
      </Stack>
    </Container>
  );
}

function VoiceAIContent() {
  const {
    fetchSettings,
    fetchPhoneNumbers,
    fetchCalls,
    fetchMessages,
    settings,
    phoneNumbers,
    phoneNumbersLoading,
    settingsLoading,
    unreadMessageCount,
  } = useVoiceAI();
  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPhoneNumbers();
    fetchCalls();
    fetchMessages();
  }, [fetchSettings, fetchPhoneNumbers, fetchCalls, fetchMessages]);

  const isLoading = phoneNumbersLoading || settingsLoading;
  const needsSetup = !isLoading && phoneNumbers.length === 0 && !setupComplete;

  if (isLoading) {
    return (
      <Container size="lg" py="md">
        <Skeleton height={40} mb="lg" />
        <Skeleton height={300} />
      </Container>
    );
  }

  if (needsSetup) {
    return <SetupWizard onComplete={() => setSetupComplete(true)} />;
  }

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Voice AI</Title>
        {settings && (
          <Badge
            color={settings.is_enabled ? "green" : "gray"}
            size="lg"
            variant="filled"
          >
            {settings.is_enabled ? "Active" : "Inactive"}
          </Badge>
        )}
      </Group>

      <Tabs defaultValue="calls">
        <Tabs.List mb="md">
          <Tabs.Tab value="calls" leftSection={<IconHistory size={16} />}>
            Call History
          </Tabs.Tab>
          <Tabs.Tab
            value="messages"
            leftSection={<IconMessage size={16} />}
            rightSection={
              unreadMessageCount > 0 ? (
                <Badge size="xs" color="red" variant="filled">
                  {unreadMessageCount}
                </Badge>
              ) : undefined
            }
          >
            Messages
          </Tabs.Tab>
          <Tabs.Tab value="phones" leftSection={<IconPhone size={16} />}>
            Phone Numbers
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={16} />}>
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="calls">
          <CallsTab />
        </Tabs.Panel>
        <Tabs.Panel value="messages">
          <MessagesTab />
        </Tabs.Panel>
        <Tabs.Panel value="phones">
          <PhoneNumbersTab />
        </Tabs.Panel>
        <Tabs.Panel value="settings">
          <SettingsTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default function VoiceAIPage() {
  return (
    <VoiceAIProvider>
      <VoiceAIContent />
    </VoiceAIProvider>
  );
}
